<template>
    <div class="card" :style="{ width: width, height: height }">
        <!-- 正面：使用雪碧图显示卡牌 -->
         
        <div class="front" :style="spriteStyle"></div>
        <!-- 背面 -->
        <div class="back" :style="{ backgroundImage: `url(${cardBackUrl})` }"></div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import cardsSpriteUrl from '../img/cards-sprite.webp';
import cardBackUrl from '../img/indoor/pocker-bg.webp';

const props = defineProps({
    width: {
        type: String,
        default: '30px',
    },
    height: {
        type: String,
        default: '40px',
    },
    isFaceUpProp: {
        type: Boolean,
        default: null
    },
    card: {
        type: Object,
        default: () => ({})
    },
});

// 雪碧图布局配置（根据 cards-sprite.webp 实际图片）
// 图片布局: 13列(A-K) x 4行(spades, hearts, clubs, diamonds)
const SPRITE_COLS = 13;
const SPRITE_ROWS = 4;

// 精灵图尺寸（单位：px）
const CARD_WIDTH = 50;       // 单张牌宽度
const CARD_HEIGHT = 50;      // 单张牌高度
const H_SPACING = 0;         // 列间距（左右间距为0）
const V_SPACING = 10;        // 行间距（上下间距为10）
const CELL_WIDTH = CARD_WIDTH + H_SPACING;   // 50 (用于计算偏移)
const CELL_HEIGHT = CARD_HEIGHT + V_SPACING; // 60 (用于计算偏移)

// 精灵图总尺寸（13张牌无水平间距，4行牌 + 3个行间距）
const SPRITE_WIDTH = SPRITE_COLS * CARD_WIDTH;   // 650px (13*50)
const SPRITE_HEIGHT = SPRITE_ROWS * CARD_HEIGHT + (SPRITE_ROWS - 1) * V_SPACING; // 230px (4*50 + 3*10)

// 花色 → 行号映射（根据雪碧图从上到下顺序）
const SUIT_TO_ROW: Record<string, number> = {
    'spades': 0,    // 黑桃 ♠ - 第1行
    'hearts': 1,    // 红桃 ♥ - 第2行
    'clubs': 2,     // 梅花 ♣ - 第3行
    'diamonds': 3,  // 方块 ♦ - 第4行
};

// 点数 → 列号映射
const RANK_TO_COL: Record<string, number> = {
    'A': 0, '2': 1, '3': 2, '4': 3, '5': 4, '6': 5, '7': 6,
    '8': 7, '9': 8, '10': 9, 'J': 10, 'Q': 11, 'K': 12,
};

const isFaceUp = ref(false);

// 内部卡牌状态（用于setCardName直接更新显示）
const internalCard = ref<{ suit: string; rank: string } | null>(null);

// 🔧 修复：当 props.card 变化时，清空 internalCard，确保使用新的 props 数据
// 原因：翻牌动画会通过 setCardName 设置 internalCard，如果不清空会导致新一局仍显示旧牌
watch(() => props.card, (newCard) => {
    if (newCard && (newCard.suit || newCard.rank)) {
        internalCard.value = null;
    }
}, { deep: true });

// 计算雪碧图背景样式
const spriteStyle = computed(() => {
    // 优先使用内部状态，否则使用props
    const cardData = internalCard.value || props.card;
    const suit = cardData?.suit || '';
    const rank = cardData?.rank || '';
    
    const row = SUIT_TO_ROW[suit];
    const col = RANK_TO_COL[rank];
    
    // 如果映射失败，默认显示第一张牌（梅花A）
    const safeRow = row !== undefined ? row : 0;
    const safeCol = col !== undefined ? col : 0;
    
    // 背景尺寸：缩放精灵图使单张牌填满容器
    const bgSizeX = (SPRITE_WIDTH / CARD_WIDTH) * 100;
    const bgSizeY = (SPRITE_HEIGHT / CARD_HEIGHT) * 100;
    
    // 背景定位：计算牌在精灵图中的实际偏移（间距只在牌之间，无边缘间距）
    const offsetX = safeCol * CELL_WIDTH;  
    const offsetY = safeRow * CELL_HEIGHT; 
    
    // CSS百分比定位公式: position% = offset / (sprite_size - card_size) * 100
    const posX = offsetX / (SPRITE_WIDTH - CARD_WIDTH) * 100;
    const posY = offsetY / (SPRITE_HEIGHT - CARD_HEIGHT) * 100;
    
    return {
        backgroundImage: `url(${cardsSpriteUrl})`,
        backgroundSize: `${bgSizeX}% ${bgSizeY}%`,
        backgroundPosition: `${posX}% ${posY}%`,
        backgroundRepeat: 'no-repeat',
    };
});

// 翻转卡片（正反面切换）
const flipCard = () => {
    isFaceUp.value = !isFaceUp.value;
};

// 切换到正面（用于GSAP动画代码兼容）
const toggleFaceUp = () => {
    isFaceUp.value = true;
};

// 切换到背面（用于GSAP动画代码兼容）
const toggleFaceDown = () => {
    isFaceUp.value = false;
};

// 设置卡牌名称并更新显示
const setCardName = (newCardName: string) => {
    isFaceUp.value = true;
    
    // 解析卡牌名称格式: "hearts10", "spadesj", "clubsa", "diamonds2"
    // 格式: {suit}s{rank} 或 {suit}{rank}
    const suitMap: Record<string, string> = {
        'hearts': 'hearts',
        'diamonds': 'diamonds',
        'clubs': 'clubs',
        'spades': 'spades'
    };
    
    let suit = '';
    let rank = '';
    
    for (const [key, value] of Object.entries(suitMap)) {
        if (newCardName.startsWith(key)) {
            suit = value;
            rank = newCardName.substring(key.length).toUpperCase();
            // 处理 'a' -> 'A', 'j' -> 'J', 'q' -> 'Q', 'k' -> 'K'
            if (rank === 'A' || rank === 'J' || rank === 'Q' || rank === 'K') {
                // 保持大写
            } else if (!isNaN(Number(rank))) {
                // 数字保持不变
            }
            break;
        }
    }
    
    if (suit && rank) {
        internalCard.value = { suit, rank };
    }
};

defineExpose({
    flipCard,
    toggleFaceUp,
    toggleFaceDown,
    setCardName,
    isFaceUp
});
</script>

<style scoped lang="scss">
.card {
    width: 30px;
    height: 40px;
    position: relative;
    perspective: 1000px;
    transform-style: preserve-3d;
    cursor: pointer;
    user-select: none;

    // 初始状态：正面朝后（背面状态），由GSAP控制翻转
    .front {
        transform: rotateY(180deg);
    }

    .back {
        transform: rotateY(0deg);
    }

    .front,
    .back {
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        backface-visibility: hidden;
        transform-origin: center center;
        overflow: hidden;
    }

    .front {
        // 雪碧图样式由 spriteStyle 计算属性控制
        // 🎨 高清渲染优化
        image-rendering: -webkit-optimize-contrast;
        image-rendering: high-quality;
        // GPU加速，减少模糊
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        // 🔧 修复：使用 translate3d 配合 rotateY，避免覆盖翻转变换
        // transform: translateZ(0); // 已移除，会覆盖上面的 rotateY(180deg)
        will-change: background-image, transform;
    }

    .back {
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;
    }
}
</style>
